'use client';

/* eslint-disable no-nested-ternary */
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import queryString from 'query-string';
import { memo, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import { Tabs } from '@startengine/design-system.ui.tabs';
import { Button } from '@startengine/design-system.ui.button';
import { Dropdown } from '@startengine/design-system.ui.dropdown';
import Box from '@mui/material/Box';
import { ResetBox } from '../layout/StyledTopNav/styles';
import Banner from './banners/Banner';
import { sellAllCategories } from './helpers';
import { dropDownStyles } from './styles';
import { clear, updateSearchField } from 'reducers/search/slice';

// Components
import {
  BannerButton,
  ExploreDesktopBannerCopy,
  ExploreMobileBannerCopy,
} from 'components/Homepage/heroChanges/SeedInvest';
import {
  PreviewBarBox,
  PromoBarBox,
  PromoBannerBg,
  PromoTypography,
} from 'components/Homepage/styles';
import { GreenLink } from 'components/theme/GreenLink';
import MainGrid from 'components/theme/MainGrid';

// Utils
import { filters } from 'utils/constants/lists';
import { trackEvent } from 'utils/analytics';

// Hooks
import useGetIndustries from 'hooks/useGetIndustries';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useGetAccreditationEligibility from 'hooks/useGetAccreditationEligibility';
import useGetElasticOfferingsInfinite from 'hooks/search/useGetElasticOfferingsInfinite';
import useTopNavHeight from 'hooks/useTopNavHeight';
import useLiveStreamsData from './liveStreams/useLiveStreamsData';
import { useHomepageVariant } from 'hooks/useHomepageVariant';
import { HomepageABTestProvider } from 'contexts/HomepageABTestContext';
import {
  HOMEPAGE_V2_FLAG,
  getBootstrapFromDocumentCookie,
  getVisitorTypeFromBootstrap,
} from 'utils/bootstrapHelpers';
import { useSafeFeatureFlagVariantKey } from 'hooks/useSafeFeatureFlag';
import ExperimentUtmInjector from 'components/Offering/ExperimentUtmInjector';

// Styles
import { getPrimaryEnv } from 'utils/helpers';

// Services

const ExploreSignedIn = dynamic(() => import('./signedIn'));

const ExploreSignedOut = dynamic(() => import('./signedOut'), {
  loading: () => (
    <Grid
      item
      container
      spacing={3}
      sx={{
        mx: 'auto',
        mt: 3,
      }}
    />
  ),
});

const ExploreSuccessStories = dynamic(() => import('./successStories'), {
  loading: () => (
    <Grid
      item
      container
      spacing={3}
      sx={{
        mx: 'auto',
        mt: 3,
      }}
    />
  ),
});

const OfferingUpdates = dynamic(() => import('./offeringUpdates'));

const LiveStreamsTab = dynamic(() => import('./liveStreams'));

// V2 Explore component
const ExploreV2 = dynamic(() => import('components/Common/ExploreV2'), {
  loading: () => (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAF8', pt: '80px' }}>
      <Box sx={{ maxWidth: '1280px', mx: 'auto', px: { xs: 2, md: 3 }, pt: 4 }}>
        <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rounded" width="100%" height={380} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  ),
  ssr: true,
});

export const RenderOfferingCircular = () => (
  <GreenLink
    link="https://www.sec.gov/Archives/edgar/data/1661779/000110465922067532/tm2217174d1_253g2.htm"
    text="Offering Circular"
    data-cy="offering-circular-link"
  />
);

const Explore = ({ serverVariant = 'control' }) => {
  const router = useRouter();
  const isSignedIn = useIsSignedIn();
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const query = useSelector((state) => state.search.query);
  const sortBySelector = useSelector((state) => state.search.sortBy);
  const mobileSeeAll = useSelector((state) => state.search.mobileSeeAll);
  const loading = useSelector((state) => state.user.loading);

  const { isV2: abTestIsV2, isResolved: isVariantResolved } = useHomepageVariant({
    serverVariant,
    pageName: pathname === '/success-stories' ? 'success-stories' : 'explore',
  });

  const posthogVariant = useSafeFeatureFlagVariantKey(HOMEPAGE_V2_FLAG);
  const isInExperiment = posthogVariant != null;

  const [visitorType] = useState(() => {
    const bootstrap = getBootstrapFromDocumentCookie();
    return getVisitorTypeFromBootstrap(bootstrap);
  });

  const { isAccredited, isQualifiedPurchaser } = useGetAccreditationEligibility({
    enabled: isSignedIn === true,
  });

  const investorExperience = (() => {
    if (!isSignedIn) return 'accredited';
    if (isQualifiedPurchaser) return 'qp';
    if (isAccredited) return 'accredited';
    return 'retail';
  })();

  const [investing, setInvesting] = useState(false);
  const isExplorePage = pathname === '/explore';
  const exploreBasePath = '/explore';
  const isSuccessStoriesPage = pathname === '/success-stories';
  const isV2 = abTestIsV2 && (isExplorePage || isSuccessStoriesPage);
  const querySelectedTab = searchParams?.get('selectedTab');
  const [selectedTab, setSelectedTab] = useState(
    querySelectedTab === 'updates' ? 1 : querySelectedTab === 'livestreams' ? 2 : 0,
  );
  // V2 renders ExploreV2 (no tabs), so skip the live-streams polling entirely
  // for V2 users instead of fetching on every mount just to hide the tab.
  const { hasAny: showLiveStreamsTab, isLoading: liveStreamsLoading } = useLiveStreamsData({
    withOfferings: false,
    enabled: !isV2,
  });
  // On a fresh deep-link to ?selectedTab=livestreams, keep the tab mounted
  // (it shows its own skeleton) while the streams query resolves, rather than
  // flashing Overview and snapping back. Only fall back once we know the tab
  // has nothing to show.
  const liveStreamsTabAvailable =
    showLiveStreamsTab || (querySelectedTab === 'livestreams' && liveStreamsLoading);
  const effectiveSelectedTab = selectedTab === 2 && !liveStreamsTabAvailable ? 0 : selectedTab;
  const category = searchParams.get('category');
  const startups = searchParams.get('startups');
  const search = searchParams.get('search');
  const categoryMainSearch = searchParams.get('categoryMainSearch');
  const sortByParam = searchParams.get('sortBy');
  const allStartups = startups === 'all';
  const [contentLoaded, setContentLoaded] = useState(false);
  const { height: topNavHeight, isReady } = useTopNavHeight();
  const [isClient, setIsClient] = useState(false);
  const hasTrackedV1PageView = useRef(false);

  const sortBy = sortBySelector || sortByParam;

  const renderSignedInView =
    !isSuccessStoriesPage &&
    !investing &&
    !query &&
    !categoryMainSearch &&
    !sortBy &&
    !allStartups &&
    !search &&
    !category;

  const showExploreBanner = !allStartups && !mobileSeeAll && !category;
  const showShowAllStartupsButton = !category && !isSuccessStoriesPage && !allStartups && !search;

  const preview = !!searchParams?.get('strapiContentId');

  const isFiltering = categoryMainSearch || sortBy;

  const { data: elasticOfferingsData } = useGetElasticOfferingsInfinite({});

  useEffect(() => {
    if (pathname === '/success-stories') {
      dispatch(
        updateSearchField({
          key: 'isMarketingPage',
          value: true,
        }),
      );
    } else {
      dispatch(
        updateSearchField({
          key: 'isMarketingPage',
          value: false,
        }),
      );
    }
  }, [dispatch, pathname]);

  useEffect(() => {
    if (query) {
      dispatch(
        updateSearchField({
          key: 'query',
          value: query,
        }),
      );
      dispatch(
        updateSearchField({
          key: 'queryInfinite',
          value: query,
        }),
      );
    }
  }, [dispatch, query]);

  useEffect(() => {
    if (!investing && !sortBy && !query && !categoryMainSearch) {
      dispatch(
        updateSearchField({
          key: 'startupLength',
          value: null,
        }),
      );
    }
    if (isSignedIn) {
      setInvesting(false);
    }
  }, [investing, sortBy, query, categoryMainSearch, isSignedIn, dispatch]);

  useEffect(() => {
    if (querySelectedTab) {
      setSelectedTab(
        querySelectedTab === 'updates' ? 1 : querySelectedTab === 'livestreams' ? 2 : 0,
      );
    }
  }, [querySelectedTab]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        setContentLoaded(true);
      }, 100);
    }
  }, [loading]);

  useEffect(() => {
    setIsClient(true);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isVariantResolved || !isExplorePage || isV2 || hasTrackedV1PageView.current) return;
    hasTrackedV1PageView.current = true;
    trackEvent('viewed_explore', {
      page: 'explore',
      explore_variant: 'v1',
      homepage_variant: 'control',
      experiment: HOMEPAGE_V2_FLAG,
      visitor_type: visitorType,
      investor_experience: investorExperience,
    });
  }, [isExplorePage, isV2, isVariantResolved, visitorType, investorExperience]);

  const handleResetMainSearch = () => {
    dispatch(clear());
    router.push(exploreBasePath);
  };

  const onFieldChange = (key, value) => {
    dispatch(
      updateSearchField({
        key,
        value,
      }),
    );

    // Clear sortBy when category changes
    if (key === 'categoryMainSearch') {
      dispatch(
        updateSearchField({
          key: 'sortBy',
          value: '',
        }),
      );
    }

    const url = queryString.stringifyUrl(
      {
        url: exploreBasePath,
        query: {
          categoryMainSearch: key === 'categoryMainSearch' ? value : categoryMainSearch || '',
          sortBy: key === 'sortBy' ? value : key === 'categoryMainSearch' ? '' : sortBy,
        },
      },
      {
        skipEmptyString: true,
      },
    );

    router.push(url);
  };

  const { data: industries } = useGetIndustries();

  const parseIndustries = (industries) => {
    const industryList = industries
      ?.map((industry) => ({
        name: industry.key,
      }))
      ?.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

    return industryList;
  };

  const renderDropdowns = () => (
    <Box
      display={{
        xs: 'none',
        md: 'flex',
      }}
      justifyContent="space-between"
      sx={{
        mt: 2,
        mb: 5,
      }}
    >
      <Box data-cy="explore-page-dropdowns" justifyContent="left" alignItems="left" display="flex">
        {industries?.data && (
          <Box
            md={6}
            sx={dropDownStyles(categoryMainSearch)}
            height={40}
            width="300px !important"
            minWidth="200px !important"
            pr={2}
          >
            <Dropdown
              selectedFilterBg
              options={
                parseIndustries(elasticOfferingsData?.pages?.[0]?.aggregations?.industries) || []
              }
              onChange={(value) => onFieldChange('categoryMainSearch', value)}
              valueKey="name"
              displayKey="name"
              value={categoryMainSearch || ''}
              placeholder="Categories"
              data-cy="explore-page-industry-dropdown"
            />
          </Box>
        )}
        <Box
          md={6}
          sx={dropDownStyles(sortBy)}
          height={40}
          maxWidth="200px !important"
          minWidth="200px !important"
        >
          <Dropdown
            selectedFilterBg
            hideDescription
            options={filters}
            placeholder="Sort by Status"
            onChange={(value) => onFieldChange('sortBy', value)}
            valueKey="label"
            displayKey="name"
            value={sortBy || ''}
            data-cy="explore-page-sort-by-dropdown"
          />
        </Box>
      </Box>
      <ResetBox
        sx={{
          display: {
            xs: 'none',
            md: 'flex',
          },
        }}
      >
        {isFiltering ? (
          <Button
            variant="contained"
            onClick={handleResetMainSearch}
            width="md"
            sx={[
              {
                width: '86px',
                height: '40px',
                backgroundColor: 'primary',
                borderRadius: '8px',
              },
            ]}
            data-cy="explore-page-main-search-reset-button"
          >
            Reset
          </Button>
        ) : null}
      </ResetBox>
    </Box>
  );

  const renderDropdownsAboveTheTombstones =
    !category && !search && !startups && !isSuccessStoriesPage;

  const renderExplore = (tabs) => {
    return (
      <Grid
        item
        xs={12}
        sx={{
          minHeight: {
            md: '100vh',
          },
          mt: {
            xs: 0,
            lg: !tabs && !startups && !category && !search && 7,
          },
          opacity: contentLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        data-cy="explore-page-grid"
      >
        {renderDropdownsAboveTheTombstones && renderDropdowns()}
        {renderSignedInView || sellAllCategories.includes(category) ? (
          <ExploreSignedIn investing={investing} setInvesting={setInvesting} />
        ) : (
          <div>
            {isSuccessStoriesPage ? (
              <ExploreSuccessStories setInvesting={setInvesting} />
            ) : (
              <ExploreSignedOut setInvesting={setInvesting} />
            )}
          </div>
        )}
      </Grid>
    );
  };

  const renderTabs = () => {
    if (!category && (!startups || querySelectedTab) && !isSuccessStoriesPage && !search) {
      return (
        <>
          <Grid item xs={12} my={2}>
            <Tabs
              value={effectiveSelectedTab}
              onChange={(_, value) => {
                const query = value === 0 ? 'explore' : value === 1 ? 'updates' : 'livestreams';
                router.push(`?selectedTab=${query}`);
              }}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="OVERVIEW" sx={{ minHeight: '44px' }} />
              <Tab label="NEWS" sx={{ minHeight: '44px' }} />
              {liveStreamsTabAvailable && (
                <Tab
                  label="LIVE STREAMS"
                  sx={{ minHeight: '44px' }}
                  data-cy="explore-tab-live-streams"
                />
              )}
            </Tabs>
          </Grid>
          {effectiveSelectedTab === 0 && renderExplore('tabs')}
          {effectiveSelectedTab === 1 && (
            <Grid item xs={12}>
              <OfferingUpdates />
            </Grid>
          )}
          {effectiveSelectedTab === 2 && liveStreamsTabAvailable && (
            <Grid item xs={12}>
              <LiveStreamsTab />
            </Grid>
          )}
        </>
      );
    }

    return renderExplore();
  };

  // Calculate margin top similar to HomepageWrapper with additional spacing
  const DEFAULT_NAV_MARGIN = 64 + 16;
  const marginTop = isClient && isReady ? topNavHeight + 16 : DEFAULT_NAV_MARGIN;

  if (isV2) {
    return (
      <HomepageABTestProvider
        variant="v2"
        visitorType={visitorType}
        investorExperience={investorExperience}
        injectExperience={isInExperiment}
      >
        <ExperimentUtmInjector variant="v2" flagKey={HOMEPAGE_V2_FLAG} requirePostHogVariantMatch />
        <Suspense
          fallback={
            <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAF8', pt: '80px' }}>
              <Box sx={{ maxWidth: '1280px', mx: 'auto', px: { xs: 2, md: 3 }, pt: 4 }}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{ mb: 3, borderRadius: 2 }}
                />
                <Grid container spacing={3}>
                  {[1, 2, 3].map((i) => (
                    <Grid item xs={12} md={4} key={i}>
                      <Skeleton
                        variant="rounded"
                        width="100%"
                        height={380}
                        sx={{ borderRadius: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          }
        >
          <ExploreV2 />
        </Suspense>
      </HomepageABTestProvider>
    );
  }

  return (
    <HomepageABTestProvider
      variant="control"
      visitorType={visitorType}
      investorExperience={investorExperience}
    >
      <ExperimentUtmInjector
        variant="control"
        flagKey={HOMEPAGE_V2_FLAG}
        requirePostHogVariantMatch
      />
      <div
        style={{
          marginTop: marginTop > 0 ? `${marginTop}px` : 0,
          transition: 'margin-top 0.2s ease-in-out',
        }}
      >
        <MainGrid
          container
          nopadding={!isSuccessStoriesPage && !category && !startups && !search}
          search={search}
          sx={{
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          {!isExplorePage && !isSuccessStoriesPage && (
            <PromoBarBox
              className="promo-banner"
              sx={{ height: '56px' }}
              data-cy="explore-promo-banner"
            >
              <PromoBannerBg>
                {!contentLoaded ? (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={40}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 0,
                    }}
                    data-cy="promo-banner-skeleton"
                  />
                ) : (
                  <>
                    <ExploreMobileBannerCopy />
                    <ExploreDesktopBannerCopy />
                    <BannerButton />
                  </>
                )}
              </PromoBannerBg>
            </PromoBarBox>
          )}
          {showExploreBanner && !search ? (
            <Grid
              item
              xs={12}
              sx={{
                px: { xs: !isSuccessStoriesPage && 2, sm: 0 },
                mt: { xs: 3, sm: 4, md: 5 },
                opacity: contentLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              <Banner preview={preview} />
            </Grid>
          ) : null}
          {!loading ? renderTabs() : null}
          {preview && (
            <PreviewBarBox data-cy="preview-mode-box">
              <PromoTypography variant="p1Bold">Preview Mode</PromoTypography>
            </PreviewBarBox>
          )}
        </MainGrid>
        {showShowAllStartupsButton && effectiveSelectedTab === 0 && (
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            sx={{
              mt: -8,
              mb: 4,
              opacity: contentLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <a
              href={`${getPrimaryEnv()}${exploreBasePath}?startups=all`}
              style={{
                textDecoration: 'none',
              }}
            >
              <Button
                variant="contained"
                sx={[
                  {
                    backgroundColor: 'black',
                    minHeight: '44px',
                    px: { xs: 4 },
                  },
                  (theme) => ({
                    '&:hover': {
                      backgroundColor: `${theme.palette.common.black} !important`,
                    },
                  }),
                ]}
                data-cy="show-all-startups-button"
              >
                Show All Startups
              </Button>
            </a>
          </Grid>
        )}
      </div>
    </HomepageABTestProvider>
  );
};

export default memo(Explore);
